import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from '../../hooks/useConsent';
import { ALL_OPTIONAL, NO_OPTIONAL } from '../../lib/consent';
import { decideConsent, onOpenPrivacySettings } from '../../lib/consentStore';
import { trackLocation } from '../../lib/tracking';
import { ConsentBanner } from './ConsentBanner';
import { PrivacySettingsDialog } from './PrivacySettingsDialog';

/**
 * The advisor's own pages, `/admin` and `/brand` and everything under them.
 * Nothing is ever measured there (the runtime switches analytics off for any
 * page outside the public site), so the banner does not ask there: it would be
 * asking about measurement that cannot happen, and the room it reserves at the
 * foot of the page shows as a blank strip under the admin shell. The choice is
 * still asked for on the public site, and the settings dialog still opens
 * from anywhere.
 */
function isStaffPath(pathname: string): boolean {
  return /^\/(admin|brand)(\/|$)/i.test(pathname);
}

/**
 * Mounted once, inside the router. Shows the banner when the visitor has to be
 * asked (never on the advisor's own pages), owns the settings dialog, and tells
 * the runtime about every location so page views go out (or do not) as the
 * visitor's choice says.
 *
 * Renders nothing on the server or while hydrating: the choice lives in this
 * browser, so the banner appears once the page has hydrated, for every visitor
 * who has not chosen.
 */
export function ConsentManager() {
  const consent = useConsent();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => onOpenPrivacySettings(() => setSettingsOpen(true)), []);

  useEffect(() => {
    trackLocation(location.key);
  }, [location.key]);

  return (
    <>
      {consent.status === 'needs_choice' && !settingsOpen && !isStaffPath(location.pathname) && (
        <ConsentBanner
          onAcceptAll={() => decideConsent(ALL_OPTIONAL, 'accept_all')}
          onRejectOptional={() => decideConsent(NO_OPTIONAL, 'reject_optional')}
          onCustomize={() => setSettingsOpen(true)}
        />
      )}
      <PrivacySettingsDialog
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        consent={consent}
        onSave={(choices, method) => {
          decideConsent(choices, method);
          setSettingsOpen(false);
        }}
      />
    </>
  );
}
